import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { usePage } from "@/settings";
import { EASE } from "@/components/Motion";

const LINKS = [
  { to: "/about", key: "about", label: "About", testid: "nav-about-link" },
  { to: "/work", key: "work", label: "Work", testid: "nav-work-link" },
  { to: "/services", key: "services", label: "Services", testid: "nav-services-link" },
  { to: "/workshops", key: "workshops", label: "Workshops", testid: "nav-workshops-link" },
  { to: "/shop", key: "shop", label: "Shop", testid: "nav-shop-link" },
  { to: "/community", key: "community", label: "Community", testid: "nav-community-link" },
  { to: "/contact", key: "contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();
  const g = usePage("global");
  const links = LINKS.map((l) => ({ ...l, label: g[`nav_${l.key}`] || l.label }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-paper/85 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-12 py-4">
          <Logo small />
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-brand ${
                    isActive ? "text-ink" : "text-ink/60"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-2">
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-brand" />}
                    {l.label}
                  </span>
                )}
              </NavLink>
            ))}
            <Link to="/cart" data-testid="nav-cart-link" className="relative text-ink hover:text-brand transition-colors">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-brand text-paper text-[10px] font-mono flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
          </nav>
          <div className="flex lg:hidden items-center gap-3">
            <Link to="/cart" data-testid="nav-cart-link-mobile" className="relative text-ink p-2" aria-label="Cart">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 rounded-full bg-brand text-paper text-[10px] font-mono flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button data-testid="nav-menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu" className="p-2 -mr-2">
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-ink text-paper flex flex-col overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-6 py-4">
              <Logo small dark />
              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 -mr-2">
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className="font-display text-4xl sm:text-5xl py-2 inline-flex items-center gap-4 hover:text-brand transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full bg-brand/40" />
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + LINKS.length * 0.06, duration: 0.6, ease: EASE }}
                className="mt-10 pt-6 border-t border-paper/15"
              >
                <NavLink
                  to="/cart"
                  data-testid="mobile-cart-link"
                  className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-paper/50 hover:text-brand transition-colors"
                >
                  <ShoppingBag size={14} strokeWidth={1.5} /> Cart{count > 0 ? ` (${count})` : ""}
                </NavLink>
              </motion.div>
            </nav>
            <p className="px-8 pb-8 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40">
              Art + Architecture + Material + Craft
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
